// src/pages/taxonomy/Sizes/SizesListPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SizesAPI } from '../../../api/v3/taxonomy-api-v3';

const BASE = '/app/sales/bagspa/taxonomy';

const includesCi = (h = '', n = '') =>
  (h || '').toString().toLowerCase().includes((n || '').toString().toLowerCase());

function formatRange(r = {}) {
  if ((r.kind || '').toLowerCase() !== 'bag') return '-';
  const lo = r.min_cm;
  const hi = r.max_cm;
  if (lo && hi) return `${lo}–${hi} cm`;
  if (lo && !hi) return `≥${lo} cm`;
  if (!lo && hi) return `≤${hi} cm`;
  return '-';
}

export default function SizesListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  // filters + pagination
  const [qName, setQName] = useState('');
  const [qCode, setQCode] = useState('');
  const [qKind, setQKind] = useState(''); // ''=all
  const [per, setPer] = useState(25);
  const [page, setPage] = useState(1);

  async function load() {
    try {
      setLoading(true);
      setError('');
      const data  = await SizesAPI.list();
      const items = data?.items ?? data?.data ?? (Array.isArray(data) ? data : []);
      setRows(items || []);
    } catch (e) {
      setError(e.message || 'Failed to fetch sizes');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const kindOptions = useMemo(() => {
    const uniq = Array.from(new Set((rows || []).map(r => (r.kind || '').toString().toLowerCase()).filter(Boolean)));
    return uniq.sort();
  }, [rows]);

  // filter
  const filtered = useMemo(() => {
    let r = rows;
    if (qName) r = r.filter(x => includesCi(x.name, qName));
    if (qCode) r = r.filter(x => includesCi(x.code, qCode));
    if (qKind) r = r.filter(x => (x.kind || '').toString().toLowerCase() === qKind.toLowerCase());
    return r;
  }, [rows, qName, qCode, qKind]);

  // pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / per));
  const pageSafe = Math.min(page, totalPages);
  const start = (pageSafe - 1) * per;
  const paged = useMemo(() => filtered.slice(start, start + per), [filtered, start, per]);

  async function handleDelete(id) {
    if (!window.confirm('Delete this size?')) return;
    try {
      await SizesAPI.destroy(id);
      const nextRows = rows.filter(r => r.id !== id);
      setRows(nextRows);
      // keep page within range
      const nextFiltered = nextRows.filter(x =>
        (!qName || includesCi(x.name, qName)) &&
        (!qCode || includesCi(x.code, qCode)) &&
        (!qKind || (x.kind || '').toString().toLowerCase() === qKind.toLowerCase())
      );
      const nextTotalPages = Math.max(1, Math.ceil(nextFiltered.length / per));
      if (pageSafe > nextTotalPages) setPage(nextTotalPages);
    } catch (e) {
      alert(e.message);
    }
  }

  function resetFilters() {
    setQName('');
    setQCode('');
    setQKind('');
    setPage(1);
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: 8, flexWrap: 'wrap' }}>
        <h3 className="mb-0">Sizes</h3>
        <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            className="form-control"
            style={{ width: 220 }}
            placeholder="Filter name…"
            value={qName}
            onChange={(e) => { setQName(e.target.value); setPage(1); }}
          />
          <input
            className="form-control"
            style={{ width: 160 }}
            placeholder="Filter code…"
            value={qCode}
            onChange={(e) => { setQCode(e.target.value); setPage(1); }}
          />
          <select
            className="form-select"
            style={{ width: 160 }}
            value={qKind}
            onChange={(e) => { setQKind(e.target.value); setPage(1); }}
            title="Filter kind (e.g., bag)"
          >
            <option value="">All kinds</option>
            {kindOptions.map(k => <option key={k} value={k}>{k || '(blank)'}</option>)}
          </select>

          <select
            className="form-select"
            style={{ width: 120 }}
            value={per}
            onChange={(e) => { setPer(parseInt(e.target.value, 10)); setPage(1); }}
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}/page</option>)}
          </select>

          <button className="btn btn-outline-secondary" onClick={resetFilters}>Reset</button>
          <Link className="btn btn-primary" to={`${BASE}/sizes/new`}>New</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-sm align-middle table-hover">
              <thead className="table-light">
                <tr>
                  <th style={{width:70}}>ID</th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Kind</th>
                  <th>Range (cm)</th>
                  <th>Definition / Notes</th>
                  <th>Active</th>
                  <th style={{width:200}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.length > 0 ? paged.map(row => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.name}</td>
                    <td><code>{row.code}</code></td>
                    <td className="text-capitalize">{row.kind || '-'}</td>
                    <td>{formatRange(row)}</td>
                    <td className="small text-muted">{row.dimension_text || '-'}</td>
                    <td>{row.active ? '✅' : '—'}</td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <Link className="btn btn-outline-primary" to={`${BASE}/sizes/${row.id}/edit`}>Edit</Link>
                        <button className="btn btn-outline-danger" onClick={() => handleDelete(row.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={8} className="text-muted">No data.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">Total: {filtered.length} • Page {pageSafe} / {totalPages}</div>
            <div className="btn-group">
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe <= 1} onClick={() => setPage(1)}>&laquo;</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>&lsaquo; Prev</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next &rsaquo;</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={pageSafe >= totalPages} onClick={() => setPage(totalPages)}>&raquo;</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
